import React, { useContext, useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { AuthContext } from "../../shared/context/auth-context";
import { markAwayAtStripe } from "../../shared/util/idle";
import { ROLE_LABELS } from "../../shared/util/roles";
import "./UserSettings.css";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    year: "numeric", month: "short", day: "numeric",
  });
}

function formatDateTime(iso) {
  return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
}

function formatMonth(iso) {
  return new Date(iso).toLocaleDateString("en-GB", { month: "short", year: "numeric" });
}

// Amount in major units (e.g. 29 → "CHF 29.00"). All plans are billed in CHF.
function formatMoney(amount, currency = "chf") {
  return new Intl.NumberFormat("en-CH", { style: "currency", currency: currency.toUpperCase() }).format(amount);
}

// The API needs the Authorization header, so a plain link can't download
// the PDF; fetch it as a blob and save it from an object URL instead.
async function downloadInvoice(token, invoiceId) {
  const res = await fetch(`/api/payments/invoices/${invoiceId}/pdf`, {
    headers: { Authorization: "Bearer " + token },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || "Download failed.");
  }
  // The server names the file after the invoice number
  const disposition = res.headers.get("Content-Disposition") || "";
  const filename    = /filename="([^"]+)"/.exec(disposition)?.[1] || "invoice.pdf";
  const url  = URL.createObjectURL(await res.blob());
  const link = document.createElement("a");
  link.href     = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ─── Sections ─────────────────────────────────────────────────────────────────

function ProfileSection({ auth }) {
  const storedData = JSON.parse(localStorage.getItem("userData") || "{}");
  return (
    <div className="us-section">
      <div className="us-field">
        <span className="us-label">User ID</span>
        <span className="us-value us-mono">{storedData.userId || "—"}</span>
      </div>
      <div className="us-field">
        <span className="us-label">Role</span>
        <span className={`badge badge--${auth.role}`}>{ROLE_LABELS[auth.role] || auth.role}</span>
      </div>
      <div className="us-field">
        <span className="us-label">Plan</span>
        {auth.isPaid ? (
          <span className="us-value">Signal Access — Monthly</span>
        ) : (
          <span className="badge badge--inactive">No active plan</span>
        )}
      </div>
      <div className="us-field">
        <span className="us-label">Status</span>
        <span className={`badge badge--${auth.isPaid ? "active" : "inactive"}`}>
          {auth.isPaid ? "Active" : "Inactive"}
        </span>
      </div>
      {auth.isPaid && (
        <div className="us-field">
          <span className="us-label">Paid until</span>
          <span className="us-value">{formatDate(auth.paidUntil)}</span>
        </div>
      )}
    </div>
  );
}

function CurrentPlanTab({ auth, justSignedUp }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const handleSubscribe = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/payments/create-checkout", {
        method:  "POST",
        headers: { Authorization: "Bearer " + auth.token },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Checkout failed.");
      markAwayAtStripe(); // the time at Stripe isn't inactivity (shared/util/idle.js)
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/payments/create-portal-session", {
        method:  "POST",
        headers: { Authorization: "Bearer " + auth.token },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Could not open billing portal.");
      markAwayAtStripe();
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="us-section">
      {error && <p className="us-hint us-hint--error">{error}</p>}

      {auth.isPaid ? (
        <>
          <div className="us-field">
            <span className="us-label">Status</span>
            <span className="badge badge--active">Active</span>
          </div>
          <div className="us-field">
            <span className="us-label">Plan</span>
            <span className="us-value">Signal Access — Monthly</span>
          </div>
          <div className="us-field">
            <span className="us-label">Paid until</span>
            <span className="us-value">{formatDate(auth.paidUntil)}</span>
          </div>
          <button
            className="settings-btn settings-btn--outline us-btn"
            onClick={handleManageBilling}
            disabled={loading}
          >
            {loading ? "Redirecting..." : "Manage billing"}
          </button>
        </>
      ) : (
        <>
          {justSignedUp && (
            <p className="us-hint us-hint--success">
              Your account is ready. Subscribe to start receiving daily trade signals by email.
            </p>
          )}
          <div className="us-field">
            <span className="us-label">Status</span>
            <span className="badge badge--inactive">Inactive</span>
          </div>
          <div className="plan-card">
            <p className="plan-card__name">Signal Access</p>
            <p className="plan-card__price">
              CHF 29<span> / month</span>
            </p>
            <ul className="plan-card__features">
              <li>Live buy / sell trade signals</li>
              <li>Active position tracking</li>
              <li>Full instrument analytics</li>
              <li>Monthly performance reports</li>
              <li>Live fund equity curve</li>
            </ul>
          </div>
          <button
            className="settings-btn us-btn"
            onClick={handleSubscribe}
            disabled={loading}
          >
            {loading ? "Redirecting..." : "Subscribe"}
          </button>
        </>
      )}
    </div>
  );
}

// How each PaymentLog action reads in the history. `source` is "stripe" for
// payments and Stripe events, "admin" for changes made by MTT.
const HISTORY_EVENTS = {
  SUBSCRIBE: { tone: "start",  title: "Subscription started",   adminTitle: "Subscription activated" },
  EXTEND:    { tone: "renew",  title: "Subscription renewed",   adminTitle: "Subscription extended"  },
  CANCEL:    { tone: "cancel", title: "Subscription cancelled", adminTitle: "Subscription cancelled" },
};

function HistoryEvent({ entry, downloading, onDownload }) {
  const kind      = HISTORY_EVENTS[entry.action] || { tone: "renew", title: entry.action, adminTitle: entry.action };
  const title     = entry.source === "admin" ? kind.adminTitle : kind.title;
  const cancelled = entry.action === "CANCEL";
  const details   = [
    entry.source === "admin" ? "Updated by MTT" : !cancelled && "Paid via Stripe",
    cancelled ? "Access ended" : entry.paidUntil && `Access until ${formatDate(entry.paidUntil)}`,
  ].filter(Boolean);

  return (
    <li className={`us-event us-event--${kind.tone}`}>
      <span className="us-event__dot" aria-hidden="true" />
      <div className="us-event__body">
        <p className="us-event__title">{title}</p>
        <p className="us-event__meta">
          <time dateTime={entry.createdAt} title={formatDateTime(entry.createdAt)}>
            {formatDate(entry.createdAt)}
          </time>
          {details.map((d) => <span key={d}> · {d}</span>)}
        </p>
      </div>
      <div className="us-event__aside">
        {entry.amount != null && !cancelled && (
          <span className="us-event__amount">{formatMoney(entry.amount)}</span>
        )}
        {entry.invoiceId && (
          <button
            className="us-link-btn us-event__invoice"
            onClick={() => onDownload(entry)}
            disabled={downloading}
            aria-label={`Download invoice: ${title}, ${formatDate(entry.createdAt)}`}
          >
            <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
              <path d="M8 2v8m0 0L4.5 6.5M8 10l3.5-3.5M3 13h10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {downloading ? "Preparing…" : "Invoice"}
          </button>
        )}
      </div>
    </li>
  );
}

function HistorySkeleton() {
  return (
    <ol className="us-timeline" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <li key={i} className="us-event">
          <span className="us-event__dot us-skeleton" />
          <div className="us-event__body">
            <span className="us-skeleton us-skeleton--title" />
            <span className="us-skeleton us-skeleton--meta" />
          </div>
        </li>
      ))}
    </ol>
  );
}

function HistoryTab({ auth, onShowPlan }) {
  const [entries, setEntries]             = useState(null); // null while loading
  const [error, setError]                 = useState(false);
  const [reloadKey, setReloadKey]         = useState(0);
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadError, setDownloadError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const fetch_ = async () => {
      setError(false);
      setEntries(null);
      try {
        const res = await fetch("/api/users/me/payment-history", {
          headers: { Authorization: "Bearer " + auth.token },
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setEntries((await res.json()).entries);
      } catch (err) {
        if (err.name !== "AbortError") setError(true);
      }
    };
    fetch_();
    return () => controller.abort();
  }, [auth.token, reloadKey]);

  const handleDownload = async (entry) => {
    setDownloadingId(entry.id);
    setDownloadError(null);
    try {
      await downloadInvoice(auth.token, entry.invoiceId);
    } catch (err) {
      setDownloadError(err.message);
    } finally {
      setDownloadingId(null);
    }
  };

  if (error) {
    return (
      <div className="us-section us-empty" role="alert">
        <p className="us-empty__title">Couldn't load your billing history</p>
        <p className="us-note">Check your connection and try again.</p>
        <button
          className="settings-btn settings-btn--outline us-empty__action"
          onClick={() => setReloadKey((k) => k + 1)}
        >
          Try again
        </button>
      </div>
    );
  }

  if (!entries) {
    return <div className="us-section" aria-busy="true"><HistorySkeleton /></div>;
  }

  if (entries.length === 0) {
    return (
      <div className="us-section us-empty">
        <svg className="us-empty__icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 3h12v18l-3-2-3 2-3-2-3 2V3zM9 8h6M9 12h6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <p className="us-empty__title">No billing activity yet</p>
        <p className="us-note">Payments, renewals and plan changes will show up here.</p>
        {!auth.isPaid && (
          <button className="settings-btn us-empty__action" onClick={onShowPlan}>View plan</button>
        )}
      </div>
    );
  }

  // Entries arrive newest first
  const payments  = entries.filter((e) => e.amount != null && e.action !== "CANCEL");
  const totalPaid = payments.reduce((sum, e) => sum + e.amount, 0);
  const starts    = entries.filter((e) => e.action === "SUBSCRIBE");
  const firstStart = starts.length ? starts[starts.length - 1].createdAt : null;

  const years = [];
  for (const entry of entries) {
    const year = new Date(entry.createdAt).getFullYear();
    if (years.length === 0 || years[years.length - 1].year !== year) years.push({ year, items: [] });
    years[years.length - 1].items.push(entry);
  }

  return (
    <div className="us-section">
      <dl className="us-stats">
        <div className="us-stat">
          <dt>Total paid</dt>
          <dd>{formatMoney(totalPaid)}</dd>
        </div>
        <div className="us-stat">
          <dt>Payments</dt>
          <dd>{payments.length}</dd>
        </div>
        <div className="us-stat">
          <dt>Customer since</dt>
          <dd>{firstStart ? formatMonth(firstStart) : "—"}</dd>
        </div>
      </dl>

      {downloadError && <p className="us-note us-note--error" role="alert">{downloadError}</p>}

      {years.map(({ year, items }) => (
        <section key={year} aria-labelledby={`us-history-${year}`}>
          <h3 id={`us-history-${year}`} className="us-timeline__year">{year}</h3>
          <ol className="us-timeline">
            {items.map((entry) => (
              <HistoryEvent
                key={entry.id}
                entry={entry}
                downloading={downloadingId === entry.id}
                onDownload={handleDownload}
              />
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}

function InvoicesTab({ auth }) {
  const [invoices, setInvoices]       = useState([]);
  const [isLoading, setIsLoading]     = useState(false);
  const [error, setError]             = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const fetch_ = async () => {
      setIsLoading(true);
      try {
        const res = await fetch("/api/payments/invoices", {
          headers: { Authorization: "Bearer " + auth.token },
          signal: controller.signal,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
        setInvoices(data.invoices);
      } catch (err) {
        if (err.name !== "AbortError") setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetch_();
    return () => controller.abort();
  }, [auth.token]);

  const handleDownload = async (invoice) => {
    setDownloadingId(invoice.id);
    setError(null);
    try {
      await downloadInvoice(auth.token, invoice.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloadingId(null);
    }
  };

  if (isLoading) return <div className="us-section"><p className="us-hint">Loading…</p></div>;

  return (
    <div className="us-section us-section--flush">
      {error && <p className="us-hint us-hint--error">{error}</p>}
      {invoices.length === 0 ? (
        !error && <p className="us-hint">No invoices yet.</p>
      ) : (
        <table className="us-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Invoice</th>
              <th>Period</th>
              <th>Amount</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id}>
                <td>{formatDate(inv.createdAt)}</td>
                <td>{inv.number}</td>
                <td>{inv.periodStart ? `${formatDate(inv.periodStart)} – ${formatDate(inv.periodEnd)}` : "—"}</td>
                <td>{formatMoney(inv.total, inv.currency)}</td>
                <td><span className={`us-badge us-badge--${inv.status}`}>{inv.status.toUpperCase()}</span></td>
                <td>
                  <button
                    className="us-link-btn"
                    onClick={() => handleDownload(inv)}
                    disabled={downloadingId === inv.id}
                  >
                    {downloadingId === inv.id ? "Preparing…" : "Download PDF"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function BillingSection({ auth, justSignedUp }) {
  const [tab, setTab] = useState("plan");

  return (
    <div>
      <div className="us-tabs">
        <button className={`us-tab ${tab === "plan"     ? "us-tab--active" : ""}`} onClick={() => setTab("plan")}>
          Current Plan
        </button>
        <button className={`us-tab ${tab === "invoices" ? "us-tab--active" : ""}`} onClick={() => setTab("invoices")}>
          Invoices
        </button>
        <button className={`us-tab ${tab === "history"  ? "us-tab--active" : ""}`} onClick={() => setTab("history")}>
          History
        </button>
      </div>
      {tab === "plan"     && <CurrentPlanTab auth={auth} justSignedUp={justSignedUp} />}
      {tab === "invoices" && <InvoicesTab    auth={auth} />}
      {tab === "history"  && <HistoryTab     auth={auth} onShowPlan={() => setTab("plan")} />}
    </div>
  );
}

// IBKR has separate logins for paper (simulated) and live trading. A user can
// store one of each and chooses which one automatic trading uses.
const TRADING_MODES = [
  {
    key: "paper", label: "Paper", name: "paper account",
    about: "Simulated trading with paper money. IBKR gives paper accounts their own username and password, separate from your live login (set up in Client Portal).",
  },
  {
    key: "live", label: "Live", name: "live account",
    about: "Your real IBKR account: orders are placed with real money. IBKR asks you to approve live logins in the IBKR Mobile app.",
  },
];
// "paper account", "live account" or "paper and live accounts"
const tradedName = (modes) => (modes.length > 1 ? "paper and live accounts" : modes[0].name);

// Loads the trading account state: { accounts: { paper, live }, autoTrading,
// liveTrading (on | dry_run | off), waitingFor }, each account { connected, usernameHint,
// updatedAt, check, checkable, checkBlocked, checksLeft, lastRun }.
function useTradingAccount(token) {
  const [account, setAccount] = useState(null);
  const [error, setError]     = useState(null);
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    const fetch_ = async () => {
      try {
        const res = await fetch("/api/users/me/trading-account", {
          headers: { Authorization: "Bearer " + token },
          signal: controller.signal,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
        setAccount(data);
      } catch (err) {
        if (err.name !== "AbortError") setError(err.message);
      }
    };
    fetch_();
    return () => controller.abort();
  }, [token, reloads]);

  const reload = () => setReloads((n) => n + 1);
  return [account, setAccount, error, reload];
}

const CHECKING = ["queued", "running"];
const CHECK_POLL_MS = 4000;

function TradingAccountSection({ auth }) {
  const [account, setAccount, loadError, reload] = useTradingAccount(auth.token);
  const [editing, setEditing] = useState(null); // mode whose form is open
  const [notice, setNotice]   = useState(null);

  // While a connection check runs, ask for its result every few seconds
  const checking = Boolean(account) && TRADING_MODES.some((m) => CHECKING.includes(account.accounts[m.key].check?.status));
  useEffect(() => {
    if (!checking) return undefined;
    const timer = setInterval(reload, CHECK_POLL_MS);
    return () => clearInterval(timer);
  }, [checking]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loadError) return <div className="us-section"><p className="us-hint us-hint--error">{loadError}</p></div>;
  if (!account)  return <div className="us-section"><p className="us-hint">Loading…</p></div>;

  // Saving and removing answer with the whole state
  const handleDone = (data, message) => {
    setAccount(data);
    setEditing(null);
    setNotice(message);
  };

  return (
    <div className="us-section">
      <AutoTradingSetting
        auth={auth}
        account={account}
        onChange={(state) => setAccount((a) => ({ ...a, ...state }))}
      />

      <h3 className="us-heading">Accounts</h3>
      <p className="us-note">
        Usernames and passwords are encrypted before they are stored. They are only used to place
        trades when automatic trading is on and to check the login, and are never shown again.
      </p>
      <ul className="us-accounts">
        {TRADING_MODES.map((mode) => (
          <TradingLogin
            key={mode.key}
            auth={auth}
            mode={mode}
            login={account.accounts[mode.key]}
            autoTrading={account.autoTrading}
            inUse={account.autoTrading && account.accounts[mode.key].connected}
            onlyLogin={TRADING_MODES.every((m) => m.key === mode.key || !account.accounts[m.key].connected)}
            waitingFor={account.waitingFor}
            open={editing === mode.key}
            onOpen={() => { setNotice(null); setEditing(mode.key); }}
            onCancel={() => setEditing(null)}
            onDone={handleDone}
            onState={(data) => { setNotice(null); setAccount(data); }}
          />
        ))}
      </ul>
      {notice && <p className="us-hint us-hint--success" role="status">{notice}</p>}
    </div>
  );
}

const RUN_NAMES = { place: "morning run", reconcile: "evening run" };

// What we know of a stored login, newest first: a check in progress, else
// the newer of the last check's result and the trader's latest login with it
function loginStatus(login) {
  if (!login.connected) return { label: "Not set up", tone: "neutral" };
  const check = login.check;
  if (CHECKING.includes(check?.status)) return { label: "Checking…", tone: "info" };
  const events = [];
  if (check?.status === "ok") events.push({ at: check.finishedAt, works: true });
  if (check?.status === "failed" && ["rejected", "timeout"].includes(check.reason)) {
    events.push({ at: check.finishedAt, works: false });
  }
  if (login.lastRun?.ok) events.push({ at: login.lastRun.at, works: true });
  if (login.lastRun?.loginFailed) events.push({ at: login.lastRun.at, works: false });
  const latest = events.sort((a, b) => new Date(b.at) - new Date(a.at))[0];
  if (latest) return latest.works ? { label: "Login works", tone: "active" } : { label: "Login failed", tone: "inactive" };
  if (check?.reason === "unapproved") return { label: "Not approved", tone: "warning" };
  if (check?.status === "failed") return { label: "Not checked", tone: "warning" };
  return { label: login.checkable ? "Saved, not checked" : "Saved", tone: "neutral" };
}

const CHECK_FAILURES = {
  rejected:    "IBKR didn't accept the username or password",
  timeout:     "IBKR didn't complete the login within 4 minutes",
  unapproved:  "IBKR accepted the password, but the login wasn't approved in IBKR Mobile within 3 minutes",
  rejected_limit: "Not tried: IBKR rejected this account's login twice today",
  unavailable: "The check couldn't run: MTT's connection to IBKR is busy or down",
  error:       "The check failed",
  expired:     "The check didn't finish",
};
// When the login didn't fail at IBKR, and another check is possible
const CHECK_RETRY = {
  unapproved: "Check again and approve the login in IBKR Mobile when it asks.",
  unavailable: "Try again later.", error: "Try again later.", expired: "Try again.",
};

// The connection check of a login: in progress, or its result. A live login
// waits for the user's approval in IBKR Mobile.
function CheckStatus({ mode, check, waitingFor, canCheck }) {
  if (!check) return null;
  if (CHECKING.includes(check.status)) {
    const live = mode.key === "live";
    return (
      <p className="us-check us-check--busy" role="status">
        <span className="us-check__spinner" aria-hidden="true" />
        {check.status === "queued" && waitingFor
          ? `Waiting for MTT's ${RUN_NAMES[waitingFor] || "run"} to finish, then logging in to IBKR…`
          : live
            ? "Logging in to IBKR… Approve the login in IBKR Mobile when it asks: IBKR waits 3 minutes."
            : "Logging in to IBKR… This takes about a minute; you can leave this page."}
      </p>
    );
  }
  if (check.status === "ok") {
    return (
      <p className="us-check us-check--ok">
        ✓ Login works{check.account && <> · <span className="us-mono">{check.account}</span></>}
        {check.currency && ` · ${check.currency}`} · checked {formatDateTime(check.finishedAt)}
      </p>
    );
  }
  const loginFailed = ["rejected", "timeout"].includes(check.reason);
  const retry = canCheck && CHECK_RETRY[check.reason];
  return (
    <>
      <p className={`us-check ${loginFailed ? "us-check--failed" : "us-check--warning"}`}>
        {loginFailed ? "✗ " : ""}{CHECK_FAILURES[check.reason] || CHECK_FAILURES.error}
        {check.finishedAt && ` · ${formatDateTime(check.finishedAt)}`}
      </p>
      {retry && <p className="us-note">{retry}</p>}
      {check.reason === "rejected" && mode.key === "live" && (
        <p className="us-note">
          Make sure it's the live username and password, not the paper ones, and that they work in Client Portal
          before you save them again: IBKR locks accounts after repeated failed logins.
        </p>
      )}
    </>
  );
}

// Why the login can't be checked now (checkBlocked from the backend)
const CHECK_BLOCKED = {
  login_failed: "To keep your account from being locked, MTT won't try this login again today. Replace it if the username or password is wrong.",
  rejected_limit: "IBKR rejected this account's login twice today. To keep it from being locked, MTT won't log in to it again before tomorrow, not even with a new login.",
  limit:        "Checked 5 times today: you can check again tomorrow.",
};

// One account: its state, Check/Replace/Remove, and the form when it's open
function TradingLogin({ auth, mode, login, autoTrading, inUse, onlyLogin, waitingFor, open, onOpen, onCancel, onDone, onState }) {
  const [removing, setRemoving] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError]       = useState(null);
  const title = `${mode.label} account`;
  const status = loginStatus(login);
  const busy = removing || starting || CHECKING.includes(login.check?.status);

  const handleCheck = async () => {
    setError(null);
    setStarting(true);
    try {
      const res = await fetch(`/api/users/me/trading-account/${mode.key}/check`, {
        method:  "POST",
        headers: { Authorization: "Bearer " + auth.token },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "The check couldn't start.");
      onState(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setStarting(false);
    }
  };

  const handleRemove = async () => {
    const question = !inUse ? `Remove your ${mode.name}?`
      : onlyLogin ? `Remove your ${mode.name}? Automatic trading uses it and will be turned off.`
      : `Remove your ${mode.name}? MTT stops trading it.`;
    if (!window.confirm(question)) return;
    setError(null);
    setRemoving(true);
    try {
      const res = await fetch(`/api/users/me/trading-account/${mode.key}`, {
        method:  "DELETE",
        headers: { Authorization: "Bearer " + auth.token },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Removing failed.");
      onDone(data, inUse && !data.autoTrading ? `${title} removed. Automatic trading is off.` : `${title} removed.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setRemoving(false);
    }
  };

  return (
    <li className="us-account">
      <div className="us-account__row">
        <div className="us-account__info">
          <span className="us-account__title">
            {title}
            <span className={`badge badge--${status.tone}`}>{status.label}</span>
            {inUse && <span className="badge badge--info">Automatic trading</span>}
          </span>
          <span className="us-account__meta">
            {login.connected ? (
              <><span className="us-mono">{login.usernameHint || "—"}</span> · updated {formatDate(login.updatedAt)}</>
            ) : mode.about}
          </span>
        </div>
        {!open && (
          <div className="us-account__actions">
            {login.connected && login.checkable && (
              <button
                type="button" className="us-link-btn" onClick={handleCheck}
                disabled={busy || Boolean(login.checkBlocked)}
                title={`Logs in to IBKR once and logs out again. This ends other sessions of this login, e.g. in TWS.${
                  mode.key === "live" ? " IBKR asks you to approve the login in IBKR Mobile." : ""} Nothing is traded in a check.`}
              >
                {starting ? "Starting…" : login.check ? "Check again" : "Check login"}
              </button>
            )}
            <button type="button" className="us-link-btn" onClick={onOpen} disabled={removing}>
              {login.connected ? "Replace" : "Add"}
            </button>
            {login.connected && (
              <button type="button" className="us-link-btn us-link-btn--danger" onClick={handleRemove} disabled={removing}>
                {removing ? "Removing…" : "Remove"}
              </button>
            )}
          </div>
        )}
      </div>
      {login.connected && login.checkable && (
        <CheckStatus mode={mode} check={login.check} waitingFor={waitingFor} canCheck={!login.checkBlocked} />
      )}
      {login.connected && CHECK_BLOCKED[login.checkBlocked] && (
        <p className="us-note">{CHECK_BLOCKED[login.checkBlocked]}</p>
      )}
      {login.lastRun && (
        <p className="us-note">
          Last used by MTT: {formatDateTime(login.lastRun.at)} ({RUN_NAMES[login.lastRun.kind] || "run"})
          {login.lastRun.ok ? " ✓" : login.lastRun.loginFailed ? " · ✗ login failed" : " · ✗ the run failed"}
        </p>
      )}
      {error && <p className="us-note us-note--error">{error}</p>}
      {open && (
        <LoginForm auth={auth} mode={mode} replacing={login.connected} autoTrading={autoTrading} onCancel={onCancel}
                   onSaved={(data) => onDone(data, CHECKING.includes(data.accounts[mode.key].check?.status) && mode.key === "live"
                     ? `${title} saved. MTT checks the login now: approve it in IBKR Mobile when IBKR asks.`
                     : `${title} saved.`)} />
      )}
    </li>
  );
}

function LoginForm({ auth, mode, replacing, autoTrading, onCancel, onSaved }) {
  const [form, setForm]                 = useState({ username: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError]               = useState(null);
  const [saving, setSaving]             = useState(false);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (autoTrading && mode.key === "live" && !replacing
        && !window.confirm("Automatic trading is on: MTT will also place orders with real money in this live account. Continue?")) return;
    setError(null);
    setSaving(true);
    try {
      const res = await fetch(`/api/users/me/trading-account/${mode.key}`, {
        method:  "PUT",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
        body:    JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Saving failed.");
      onSaved(data);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    // autoComplete="off": these are broker credentials, so the browser
    // shouldn't fill in (or offer to save) the MTT login here
    <form className="us-account__form" onSubmit={handleSubmit} autoComplete="off" aria-label={`${mode.label} account login`}>
      {replacing && <p className="us-note">{mode.about}</p>}
      <label className="us-field" style={{ flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
        <span className="us-label">{replacing ? "New username" : "Username"}</span>
        <input
          type="text" name="username" value={form.username}
          onChange={handleChange} autoComplete="off" autoCapitalize="none" spellCheck={false}
          className="us-input" maxLength={100} required autoFocus
        />
      </label>
      <label className="us-field" style={{ flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
        <span className="us-label">{replacing ? "New password" : "Password"}</span>
        <input
          type={showPassword ? "text" : "password"} name="password" value={form.password}
          onChange={handleChange} autoComplete="off"
          className="us-input" maxLength={200} required
        />
      </label>
      <label className="us-checkbox">
        <input
          type="checkbox"
          checked={showPassword}
          onChange={(e) => setShowPassword(e.target.checked)}
        />
        Show password
      </label>
      {error && <p className="us-note us-note--error">{error}</p>}
      <div className="us-account__buttons">
        <button className="settings-btn us-btn" type="submit" disabled={saving}>
          {saving ? "Saving…" : `Save ${mode.name}`}
        </button>
        <button className="settings-btn settings-btn--outline us-btn" type="button" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// While this is on, the trader (trader/README.md) trades every account with a
// login: paper, live or both.
function AutoTradingSetting({ auth, account, onChange }) {
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  const traded = TRADING_MODES.filter((m) => account.accounts[m.key].connected);
  const live = traded.some((m) => m.key === "live");
  // The accounts MTT trades or trial-runs: live ones only once MTT trades live accounts
  const active = traded.filter((m) => m.key !== "live" || account.liveTrading !== "off");

  const update = async (enabled) => {
    if (enabled && live && !window.confirm("Automatic trading will place orders with real money in your live account. Continue?")) return;
    setError(null);
    setSaving(true);
    try {
      const res = await fetch("/api/users/me/auto-trading", {
        method:  "PATCH",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
        body:    JSON.stringify({ enabled }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Update failed.");
      onChange(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="us-autotrading">
      <div className="us-field">
        <span className="us-label">Automatic trading</span>
        <label className="us-switch">
          <input
            type="checkbox" role="switch" aria-label="Automatic trading"
            checked={account.autoTrading}
            disabled={saving || (!traded.length && !account.autoTrading)}
            onChange={(e) => update(e.target.checked)}
          />
          <span className="us-switch__track" />
        </label>
      </div>
      <p className="us-note">
        {!traded.length
          ? "Allow MTT to place trades from the trading signals in your trading accounts. Add a paper or live account below to turn this on."
          : active.length
            ? `${account.autoTrading ? "MTT places" : "Allow MTT to place"} trades from the trading signals in your ${tradedName(active)}.`
            : "MTT doesn't trade live accounts yet: until it does, your live login is only checked."}
      </p>
      {account.autoTrading && live && account.liveTrading === "on" && (
        <p className="us-note us-note--warning">
          Live trading is on: orders use real money. MTT logs in to your live account on weekdays around 07:00 and
          23:00; approve each login in IBKR Mobile when IBKR asks.
        </p>
      )}
      {account.autoTrading && live && account.liveTrading === "dry_run" && (
        <p className="us-note">
          Live accounts are in a trial run: MTT logs in to your live account on weekdays around 07:00 and 23:00
          (approve each login in IBKR Mobile) and records the orders it would place, without sending them.
        </p>
      )}
      {live && active.length > 0 && account.liveTrading === "off" && (
        <p className="us-note">MTT doesn't trade live accounts yet: your live login is only checked.</p>
      )}
      {error && <p className="us-note us-note--error">{error}</p>}
    </div>
  );
}

// Which emails the user gets (backend/util/notification-rules.js): the
// signals, or while MTT trades their account the order emails instead.
function EmailSettings({ auth }) {
  const [prefs, setPrefs]   = useState(null);
  const [error, setError]   = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res  = await fetch("/api/users/me/email-preferences", { headers: { Authorization: "Bearer " + auth.token } });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || "Loading your email settings failed.");
        if (!cancelled) setPrefs(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [auth.token]);

  const update = async (changes) => {
    setError(null);
    setSaving(true);
    try {
      const res = await fetch("/api/users/me/email-preferences", {
        method:  "PATCH",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
        body:    JSON.stringify(changes),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Saving failed.");
      setPrefs(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) {
    return error ? <p className="us-note us-note--error">{error}</p> : null;
  }
  const traded = prefs.trading.length ? tradedName(TRADING_MODES.filter((m) => prefs.trading.includes(m.key))) : "account";
  return (
    <div className="us-emails">
      <h3 className="us-heading">Emails</h3>
      <div className="us-field">
        <span className="us-label">Signals of the day</span>
        <label className="us-switch">
          <input
            type="checkbox" role="switch" aria-label="Signals of the day"
            checked={prefs.signals.enabled} disabled={saving}
            onChange={(e) => update({ signals: e.target.checked })}
          />
          <span className="us-switch__track" />
        </label>
      </div>
      <p className="us-note">
        {prefs.trading.length
          ? `BUY, RE-BUY and SELL signals every trading day. Off by default while MTT trades your ${traded}, since you get the order emails instead.`
          : "BUY, RE-BUY and SELL signals every trading day."}
      </p>
      <div className="us-field">
        <span className="us-label">Orders placed and cleared</span>
        <label className="us-switch">
          <input
            type="checkbox" role="switch" aria-label="Orders placed and cleared"
            checked={prefs.orders.enabled} disabled={saving}
            onChange={(e) => update({ orders: e.target.checked })}
          />
          <span className="us-switch__track" />
        </label>
      </div>
      <p className="us-note">
        While MTT trades your IBKR account: the orders placed each morning, and in the evening what was filled.
      </p>
      <div className="us-field">
        <span className="us-label">Monthly statement</span>
        <label className="us-switch">
          <input
            type="checkbox" role="switch" aria-label="Monthly statement"
            checked={Boolean(prefs.statements?.enabled)} disabled={saving}
            onChange={(e) => update({ statements: e.target.checked })}
          />
          <span className="us-switch__track" />
        </label>
      </div>
      <p className="us-note">
        Last month's account report as a PDF, on the 2nd of each month: one for each IBKR account MTT trades. Any period is
        also on the live fund page.
      </p>
      {!prefs.paid && !prefs.admin && (
        <p className="us-note us-note--warning">Emails come with the subscription: you get them while it's active.</p>
      )}
      {error && <p className="us-note us-note--error">{error}</p>}
    </div>
  );
}

function SettingsSection({ auth }) {
  const [form, setForm]       = useState({ current: "", next: "", confirm: "" });
  const [status, setStatus]   = useState(null); // { type: "success"|"error", msg }
  const [loading, setLoading] = useState(false);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);

    if (form.next !== form.confirm) {
      setStatus({ type: "error", msg: "New passwords do not match." });
      return;
    }
    if (form.next.length < 8 || form.next.length > 72) {
      setStatus({ type: "error", msg: "New password must be between 8 and 72 characters." });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/users/me/password", {
        method:  "PATCH",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
        body:    JSON.stringify({ currentPassword: form.current, newPassword: form.next }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Request failed.");
      setStatus({ type: "success", msg: "Password updated successfully." });
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      setStatus({ type: "error", msg: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="us-section">
      <EmailSettings auth={auth} />
      <h3 className="us-heading">Password</h3>
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 320 }}>
        <label className="us-field" style={{ flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
          <span className="us-label">Current password</span>
          <input
            type="password" name="current" value={form.current}
            onChange={handleChange} autoComplete="current-password"
            className="us-input" required
          />
        </label>
        <label className="us-field" style={{ flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
          <span className="us-label">New password</span>
          <input
            type="password" name="next" value={form.next}
            onChange={handleChange} autoComplete="new-password"
            className="us-input" required
          />
        </label>
        <label className="us-field" style={{ flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
          <span className="us-label">Confirm new password</span>
          <input
            type="password" name="confirm" value={form.confirm}
            onChange={handleChange} autoComplete="new-password"
            className="us-input" required
          />
        </label>
        {status && (
          <p className={`us-hint ${status.type === "error" ? "us-hint--error" : "us-hint--success"}`}>
            {status.msg}
          </p>
        )}
        <button className="settings-btn us-btn" type="submit" disabled={loading}>
          {loading ? "Saving…" : "Update password"}
        </button>
      </form>
    </div>
  );
}

// ─── Nav ──────────────────────────────────────────────────────────────────────

const NAV = [
  { key: "profile",  label: "Profile"  },
  { key: "billing",  label: "Billing"  },
  { key: "trading",  label: "Trading Account" },
  { key: "settings", label: "Settings" },
];

// ─── Main page ────────────────────────────────────────────────────────────────

const UserSettings = () => {
  const auth = useContext(AuthContext);
  // /usersettings?tab=billing opens a section directly (used after signup)
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const [active, setActive] = useState(
    NAV.some(({ key }) => key === requestedTab) ? requestedTab : "profile"
  );
  const justSignedUp = !!useLocation().state?.justSignedUp;

  return (
    <div className="us-page">
      <div className="us-card">

        {/* ── Left sidebar ── */}
        <div className="us-sidebar">
          <div className="us-sidebar-header">
            <p className="us-sidebar-name">
              {JSON.parse(localStorage.getItem("userData") || "{}").name || "—"}
            </p>
            <p className="us-sidebar-title">Account Info</p>
          </div>
          <nav className="us-nav">
            {NAV.map(({ key, label }) => (
              <button
                key={key}
                className={`us-nav-btn ${active === key ? "us-nav-btn--active" : ""}`}
                onClick={() => setActive(key)}
              >
                {label}
              </button>
            ))}
          </nav>
        </div>

        {/* ── Right content ── */}
        <div className="us-body">
          {active === "profile"  && <ProfileSection  auth={auth} />}
          {active === "billing"  && <BillingSection  auth={auth} justSignedUp={justSignedUp} />}
          {active === "trading"  && <TradingAccountSection auth={auth} />}
          {active === "settings" && <SettingsSection auth={auth} />}
        </div>

      </div>
    </div>
  );
};

export default UserSettings;
